import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import {
  deleteAllergen,
  getAllergens,
  updateAllergenStatus,
} from "store/actions/allergensAction"
import placeholderImage from "assets/images/placeholderImage.png"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"

const Allergens = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Allergens List | Sign Burger")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    allergens,
  } = useSelector(state => state.Allergens)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      24,
      "allergen_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      24,
      "allergen_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      24,
      "allergen_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      24,
      "allergen_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    status: 2,
    sortOrder: 1,
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  useEffect(() => {
    fetchAllAllergens()
  }, [])

  const fetchAllAllergens = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllergens(payload))
    setIsApiCalled(true)
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    return dispatch(getAllergens(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/allergens?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall() // Default to page 1 for the initial search
  }

  const onPageChange = page => {
    getApiCall(page) // Use the specific page number for pagination
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllergens(payload))
    navigate(`/allergens/?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleStatusUpdate = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateAllergenStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteAllergen(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Allergens Lists")}
                isAddBtn={loading ? false : usersPermissions.create}
                addBtnText={t("Add Allergen")}
                onAddBtnClick={() => navigate("/allergens/add")}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                />

                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : allergens.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No allergens found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th style={{ maxWidth: 40 }}>{t("S. No.")}</th>
                          <th>{t("Image")}</th>
                          <th>{t("Name(English)")}</th>
                          <th>{t("Name(عربي)")}</th>
                          {/* <th>Image Url Type</th> */}
                          <th>{t("Status")}</th>
                          <th className="text-end">
                            <span
                              className="d-inline-block "
                              style={{
                                paddingRight: 30,
                                display: "block",
                              }}
                            >
                              {t("Action")}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {allergens.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(
                                `/allergens/allergen-details?id=${item._id}`
                              )
                            }}
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>
                              <img
                                src={
                                  item.imageUrl
                                    ? item.imageUrl
                                    : placeholderImage
                                }
                                alt=""
                                className="list_img"
                              />
                            </td>
                            <td>{item.name}</td>
                            <td>{item.nameAr}</td>
                            {/* <td>{item.imageUrlType}</td> */}
                            <td>
                              {item.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      handleStatusUpdate(item._id, item.status)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isLoading
                                    }
                                  />
                                )}
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="card-drop"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    {usersPermissions.edit && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/allergens/edit?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                          `/allergens/allergen-details?id=${item._id}`
                                        )
                                      }}
                                    >
                                      <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                      {t("View")}
                                    </DropdownItem>
                                    {usersPermissions.delete && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(item._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                            {/* )} */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && allergens.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={allergens}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Allergen"),
        })}
      />
    </div>
  )
}

export default Allergens
